/**
 * Control Tower Audit Ledger (P12)
 *
 * Append-only audit of every override action performed in the Control Tower.
 * Captures eventType, actor, target, before/after state, reason.
 *
 * Separate from controlPlane audit (governance) and from FrictionLog
 * (human-reported friction).
 */
import { supabase } from "@/integrations/supabase/client";

export type ControlTowerAuditEventType =
  | "override_mode_activated"
  | "override_mode_expired"
  | "override_mode_cancelled"
  | "kill_switch_engaged"
  | "rate_limit_blocked"
  | "assign_contractor"
  | "reassign_contractor"
  | "withdraw_offer"
  | "resend_offer"
  | "set_schedule_date"
  | "clear_schedule_date";

export interface ControlTowerAuditEntry {
  id: string;
  eventType: ControlTowerAuditEventType;
  adminId: string;
  timestamp: string;
  reason?: string;
  jobId?: string;
  contractorId?: string;
  offerId?: string;
  beforeState?: Record<string, unknown>;
  afterState?: Record<string, unknown>;
}

const MAX_LOG = 1000;
const log: ControlTowerAuditEntry[] = [];

let counter = 0;
function nextId(): string {
  counter += 1;
  return `cta-${Date.now()}-${counter}`;
}

export function recordControlTowerAudit(
  entry: Omit<ControlTowerAuditEntry, "id" | "timestamp"> & { timestamp?: string },
): ControlTowerAuditEntry {
  const full: ControlTowerAuditEntry = {
    id: nextId(),
    timestamp: entry.timestamp ?? new Date().toISOString(),
    ...entry,
  };
  log.push(full);
  if (log.length > MAX_LOG) log.shift();
  void persistAuditToDb(full);
  return full;
}

async function persistAuditToDb(entry: ControlTowerAuditEntry): Promise<void> {
  try {
    const metadata: Record<string, unknown> = {
      event_type: entry.eventType,
      offer_id: entry.offerId ?? null,
      before_state: entry.beforeState ?? null,
      after_state: entry.afterState ?? null,
      timestamp: entry.timestamp,
    };
    const { error } = await supabase.from("override_log").insert({
      rule_code: entry.eventType,
      user_id: entry.adminId,
      role: "super_admin",
      quote_id: null,
      job_id: entry.jobId ?? null,
      outcome: "overridden",
      override_reason: entry.reason ?? null,
      original_value: entry.beforeState ?? null,
      override_value: entry.afterState ?? null,
      metadata,
      source: "control_tower",
      entity_type: entry.contractorId ? "contractor" : entry.jobId ? "job" : "system",
      entity_id: entry.contractorId ?? entry.jobId ?? entry.offerId ?? null,
      action: entry.eventType,
      before_state: entry.beforeState ?? null,
      after_state: entry.afterState ?? null,
      admin_id: entry.adminId,
    });
    if (error) {
      console.warn("[controlTower.audit] DB persist failed:", error.message);
    }
  } catch (error) {
    console.warn("[controlTower.audit] DB persist threw:", error);
  }
}

export function getControlTowerAudit(): ReadonlyArray<ControlTowerAuditEntry> {
  return log;
}

export function getAuditForEntity(
  ids: { jobId?: string; contractorId?: string; offerId?: string },
): ReadonlyArray<ControlTowerAuditEntry> {
  return log.filter(
    (e) =>
      (ids.jobId && e.jobId === ids.jobId) ||
      (ids.contractorId && e.contractorId === ids.contractorId) ||
      (ids.offerId && e.offerId === ids.offerId),
  );
}

export function clearControlTowerAudit(): void {
  log.length = 0;
}

/**
 * P12.1 — Override action event types.
 * Session lifecycle events (activated/expired/cancelled/kill_switch/rate_limit_blocked)
 * are NOT counted — they are not overrides themselves.
 */
export const OVERRIDE_ACTION_EVENT_TYPES: ReadonlyArray<ControlTowerAuditEventType> = [
  "assign_contractor",
  "reassign_contractor",
  "withdraw_offer",
  "resend_offer",
  "set_schedule_date",
  "clear_schedule_date",
];

/**
 * P12.1 — Overrides per admin in the last N hours (default 24h).
 * Returns array sorted by count desc, then adminId asc for stable display.
 * Counts only true override action events.
 */
export function getOverridesPerAdmin(
  windowHours = 24,
  nowMs: number = Date.now(),
): ReadonlyArray<{ adminId: string; count: number }> {
  const cutoff = nowMs - windowHours * 60 * 60_000;
  const counts = new Map<string, number>();
  for (const e of log) {
    if (!OVERRIDE_ACTION_EVENT_TYPES.includes(e.eventType)) continue;
    if (new Date(e.timestamp).getTime() < cutoff) continue;
    counts.set(e.adminId, (counts.get(e.adminId) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([adminId, count]) => ({ adminId, count }))
    .sort((a, b) => b.count - a.count || a.adminId.localeCompare(b.adminId));
}
